import ReactGA from "react-ga4";
import { gaConfig, gtmConfig } from "src/config";
import { gtm } from "src/libs/gtm";
import {
  hasAnalyticsConsent,
  readConsent,
  type CookieConsent,
} from "src/libs/cookie-consent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let googleTrackingStarted = false;

export function isAnalyticsEnvEnabled(): boolean {
  return process.env.REACT_APP_ENV !== "development";
}

export function getGaMeasurementId(): string | undefined {
  const id = gaConfig.measurementId?.trim();
  return id || undefined;
}

function ensureGtag(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
}

export function applyDefaultDeniedConsent(): void {
  if (typeof window === "undefined") {
    return;
  }
  ensureGtag();
  window.gtag?.("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
}

function updateGoogleConsent(granted: boolean): void {
  ensureGtag();
  const value = granted ? "granted" : "denied";
  window.gtag?.("consent", "update", {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

function startGoogleTracking(): void {
  if (googleTrackingStarted || !isAnalyticsEnvEnabled()) {
    return;
  }
  const measurementId = getGaMeasurementId();
  if (measurementId) {
    ReactGA.initialize(measurementId);
  }
  if (gtmConfig.containerId) {
    gtm.initialize(gtmConfig);
  }
  googleTrackingStarted = Boolean(measurementId || gtmConfig.containerId);
}

export function applyConsentToTracking(consent: CookieConsent): void {
  if (!isAnalyticsEnvEnabled()) {
    return;
  }
  updateGoogleConsent(consent.analytics);
  if (consent.analytics) {
    startGoogleTracking();
  }
}

export function bootstrapAnalyticsConsent(): void {
  applyDefaultDeniedConsent();
  const stored = readConsent();
  if (stored) {
    applyConsentToTracking(stored);
  }
}

export function canSendGaEvents(): boolean {
  return (
    isAnalyticsEnvEnabled() &&
    hasAnalyticsConsent() &&
    Boolean(getGaMeasurementId()) &&
    googleTrackingStarted
  );
}

export function sendCurrentPageView(): void {
  if (!canSendGaEvents()) {
    return;
  }
  ReactGA.send({
    hitType: "pageview",
    page: window.location.pathname + window.location.search,
    location: window.location.href,
    referrer: document.referrer || undefined,
  });
}

export function reportAdsConversion(
  sendTo: string,
  extra?: Record<string, string>
): void {
  if (!isAnalyticsEnvEnabled() || !hasAnalyticsConsent()) {
    return;
  }
  if (typeof window.gtag !== "function") {
    return;
  }
  window.gtag("event", "conversion", {
    send_to: sendTo,
    ...extra,
  });
}
